import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LedgerAccountsSearch } from '~/routes/(main)/settings/masterdata/ledger-accounts/-searchParams';

import { useQuerySavedFilters } from '~/data/savedFilter';

import {
  createUniversalSavedFilterUrlSync,
  UNIVERSAL_FILTER_TYPES,
} from '~/modules/universalFilters';

import { getLedgerAccountsFilterConfig } from './ledgerAccountsFiltersConfig';

const LedgerAccountsRoute = getRouteApi(
  '/(main)/settings/masterdata/ledger-accounts/',
);

/**
 * Owns the ledger accounts page's state: the filter config, the saved-filter
 * URL sync, and the create/edit dialog's open state. The deep-linked
 * `ledgerAccountId` is only passed through -- loading the account it points
 * to is `LedgerAccountForm`'s job, not the page's.
 */
export function useLedgerAccountsPage() {
  const { t } = useTranslation('ledgerAccounts');

  const navigate = LedgerAccountsRoute.useNavigate();
  const savedFilterId = LedgerAccountsRoute.useSearch({
    select: ({ savedFilterId }) => savedFilterId,
  });
  const ledgerAccountId = LedgerAccountsRoute.useSearch({
    select: ({ ledgerAccountId }) => ledgerAccountId,
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const filterConfig = useMemo(() => getLedgerAccountsFilterConfig(t), [t]);

  const savedFilterUrlSync = useMemo(
    () =>
      createUniversalSavedFilterUrlSync<LedgerAccountsSearch>({
        savedFilterId,
        writeSearch: (writeSearch) => navigate({ search: writeSearch }),
      }),
    [navigate, savedFilterId],
  );

  const {
    data: savedFilters = [],
    error: savedFiltersError,
    isFetching: isSavedFiltersFetching,
    isLoading: isSavedFiltersLoading,
  } = useQuerySavedFilters(
    {
      type: UNIVERSAL_FILTER_TYPES.ledgerAccounts,
    },
    {
      enabled: Boolean(filterConfig.savedFilters?.enabled),
      meta: { suppressErrorForErrorCodes: [422] },
      select: (response) => response.data,
    },
  );

  const openCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const openImportDialog = useCallback(() => {
    setIsImportDialogOpen(true);
  }, []);

  const closeImportDialog = useCallback(() => {
    setIsImportDialogOpen(false);
  }, []);

  const closeDialog = useCallback(() => {
    setIsCreateDialogOpen(false);

    if (ledgerAccountId) {
      void navigate({
        search: (prev) => ({
          ...prev,
          ledgerAccountId: undefined,
        }),
      });
    }
  }, [ledgerAccountId, navigate]);

  const savedFiltersState = useMemo(
    () => ({
      error: savedFiltersError,
      filters: savedFilters,
      isFetching: isSavedFiltersFetching,
      isLoading: isSavedFiltersLoading,
    }),
    [
      savedFilters,
      savedFiltersError,
      isSavedFiltersFetching,
      isSavedFiltersLoading,
    ],
  );

  return {
    closeDialog,
    closeImportDialog,
    filterConfig,
    isDialogOpen: isCreateDialogOpen || Boolean(ledgerAccountId),
    isImportDialogOpen,
    ledgerAccountId,
    openCreateDialog,
    openImportDialog,
    savedFiltersState,
    savedFilterUrlSync,
  };
}
